import { Button, CircularProgress, Stack, TextField, Typography } from "@mui/material"
import React, { useEffect } from "react"
import { Tag } from "../../models"
import CopilotTooltip from "../../components/CopilotTooltip"
import { CustomPromptScope } from "../../types"
import { CopilotInputItems, InputItem } from "../../components/CopilotInputItems"
import CheckIcon from '@mui/icons-material/Check';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { CopilotStore } from "../../CopilotStore"

type TestPromptTabProps = {
    store: CopilotStore
    promptText: string
    customPromptValid: boolean
    setCustomPromptValid: Function
    customPromptTested: boolean
    setCustomPromptTested: Function
    processingStatus: boolean
    setProcessingStatus: Function
    selectedPromptModel?: Tag
    promptScope: CustomPromptScope
}

export const TestPromptTab = (testTabProps: TestPromptTabProps) => {

    const {store, 
        promptText, 
        customPromptValid,
        setCustomPromptValid,
        customPromptTested,
        setCustomPromptTested,
        processingStatus,
        setProcessingStatus,
        selectedPromptModel,
        promptScope} = testTabProps

    const {
        selectedCustomer: customer,
        backend,
        models
    } = store

    const [testQuestion, setTestQuestion] = React.useState<string>();
    const [testResponse, setTestResponse] = React.useState<string>('');
    const [validationError, setValidationError] = React.useState<string>('');
    const [testModel, setTestModel] = React.useState<Tag | undefined>(selectedPromptModel);

    useEffect(()=>{
        if(promptScope==='customer') setTestModel(undefined)
        else setTestModel(selectedPromptModel)
    },[selectedPromptModel, promptScope])

    const validatePromptRequest = async () => {
        const response = await backend?.validateCustomPrompt(customer?.name, promptText? promptText: '')
        if(response !== null)
            return response
    }

    const testPromptRequest = async () => {
        const response = await backend?.testCustomPrompt(customer?.name || "", 
                                                        testModel?.value || "",
                                                        promptText, 
                                                        testQuestion || '')
        if(response !== null)
            return response
        else
            return ''
    }
    
    const inputItems: InputItem[] =[{
        type: 'selection',
        label: '',
        onChange: (item: any) => {
            const selectedModel = models.find(model => model.rowid === item)
            setTestModel(selectedModel)
        },
        items: models.map(tc => ({ value: tc.rowid, label: tc.value })),
        selectedItem: testModel?.rowid,
        isReady: () => true,
        showLoading: () => false,
        disabled: processingStatus
    }]

    const testDisabledTooltip = () => {
        if(!testQuestion || !promptText) return "You must enter text for the custom prompt before testing it."
        else if(!testModel) return "You must select a model first."
        else return ""
    }

    const testDisabled = () => {
        return !promptText || testQuestion === '' || testQuestion === undefined || processingStatus || !testModel
    }

    const testPromptClick = async () => {
        // first we validate the prompt, then we run the test with the question
        setProcessingStatus(true)
        let testPrompt = false
        if(!customPromptValid) await validatePromptRequest()
            .then((response) => {
                if(response?.success) {
                    setCustomPromptValid(true)
                    testPrompt = true
                }
                else setCustomPromptValid(false)
                setValidationError(response?.message || '')
                setCustomPromptTested(true)
            })
        else testPrompt = true
        
        if(testPrompt) await testPromptRequest().then((response) => {
            setProcessingStatus(false)
            if(response!==undefined) {
                setTestResponse(response)
            }
        })
        else setProcessingStatus(false)
    }

    return (
        <Stack className='TestPromptTabContainer'>
            <Typography className="TestPromptContainer">
                <TextField className='TestQuestion' variant="standard"
                        placeholder={`Type your question here`} 
                        InputProps={{
                            disableUnderline: true,
                            style: {fontWeight: 400}
                          }}
                        value={testQuestion} 
                        onChange={(target?: any) =>  setTestQuestion(target.currentTarget.value)}>
                {testQuestion}
                </TextField>
                <CopilotTooltip 
                title={testDisabledTooltip()}
                disableHoverListener={!testDisabled() || processingStatus}
                placement="left">
                    <span>
                        <Button
                            disabled={testDisabled()}
                            size="medium"
                            style={{ color: testDisabled() ? '#769ffb' : '#2468FF' }}
                            onClick={()=>testPromptClick()}>
                                Test Prompt
                        </Button>
                    </span>
                </CopilotTooltip>
            </Typography>
            {promptScope==='customer' && 
            <CopilotInputItems inputItems={inputItems} />
            }
            {customPromptTested && <>
                <Typography className='ValidationContainer'>
                    <span style={{color: "#050F29", fontWeight: 500, fontSize: "16px"}}>
                        Validation status:
                    </span>
                    {customPromptValid? 
                    <CheckIcon className='IconClass' fontSize='medium' style={{color: "#76CA95"}}></CheckIcon>:
                    <ReportGmailerrorredIcon className='IconClass' style={{color: "#ED4D4D"}}></ReportGmailerrorredIcon>}
                    <span style={{color: customPromptValid? "#76CA95": "#ED4D4D", fontWeight: 400, fontSize: "14px"}}>
                        {customPromptValid? "No issues found.": "Issues detected"}
                    </span>
                </Typography>
                {!customPromptValid && <Typography className='testOutput'>
                    {validationError}
                </Typography>}
            </>}
            {processingStatus? <CircularProgress
                        size={50}
                        style={{position: 'relative',
                            top: '30%',
                            left: '48%',
                            marginTop: '-60px'}}
            /> :
            customPromptValid && <Typography className='testOutput'>
                <span style={{fontSize: "16px", fontWeight: 500, paddingBottom: "15px"}}>
                Answer:
                </span>
                <span  style={{fontSize: "13px", fontWeight: 300, whiteSpace: "pre-wrap"}}>
                {testResponse}
                </span>
            </Typography>}
        </Stack>
    )
}